import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  FORM_CONTROL_OPEN_CLASS,
  FORM_FILTER_HOVER_CLASS,
} from '@/components/ui/form-control-styles'
import { useDebounce } from '@/hooks/useDebounce'
import { clientsApi } from '@/services/clients'
import { PICKER_LIMIT } from '@/lib/listFetch'
import { ClientNameWithProspect } from '@/components/shared/StatusBadges'
import { cn } from '@/lib/utils'

interface Props {
  // Selected client ids (controlled).
  selected: string[]
  onChange: (ids: string[]) => void
  triggerClassName?: string
}

// Searchable multi-select for the invoice list client filter — same UX as the
// agenda technician filter (button + popover, checkboxes, "X selected" label,
// clear link), but with debounced server-side search since the client list can
// be large (never fetch-all).
export function ClientMultiSelectFilter({
  selected,
  onChange,
  triggerClassName,
}: Props) {
  const { t } = useTranslation('invoices')
  const [open, setOpen] = useState(false)
  const [term, setTerm] = useState('')
  const debounced = useDebounce(term, 300)
  const ref = useRef<HTMLDivElement>(null)
  const openRef = useRef(open)

  useEffect(() => {
    openRef.current = open
  }, [open])

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (!openRef.current) return
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    function onKey(e: KeyboardEvent) {
      if (!openRef.current) return
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [])

  const { data, isFetching, isError } = useQuery({
    queryKey: ['clients', 'multi-filter', debounced],
    queryFn: () =>
      clientsApi.list({
        search: debounced,
        limit: PICKER_LIMIT,
        sort_column: 'name',
        sort_order: 'asc',
      }),
    enabled: open,
  })
  const clients = data?.data ?? []

  const toggle = (id: string) =>
    onChange(
      selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]
    )

  const label =
    selected.length === 0
      ? t('list.clientFilterPlaceholder')
      : t('list.clientFilterSelected', { count: selected.length })

  return (
    <div ref={ref} className='relative'>
      <Button
        type='button'
        variant='filter'
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className={cn(
          triggerClassName ??
            'h-10 w-[220px] justify-between gap-2 rounded-lg border-input py-3 pl-4 pr-3 text-sm font-normal [&_svg]:size-4',
          FORM_FILTER_HOVER_CLASS,
          open && FORM_CONTROL_OPEN_CLASS
        )}
      >
        <span className='truncate'>{label}</span>
        <ChevronDown className='ml-2 size-4 shrink-0 opacity-60' />
      </Button>

      {open && (
        <div className='absolute left-0 z-50 mt-1 w-[260px] rounded-md border border-border bg-popover p-2 text-popover-foreground shadow-md'>
          <div className='relative mb-2'>
            <Search className='absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' />
            <input
              autoFocus
              value={term}
              onChange={e => setTerm(e.target.value)}
              placeholder={t('list.clientFilterSearch')}
              className='h-9 w-full rounded-md border border-input bg-transparent pl-8 pr-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring'
            />
          </div>

          {selected.length > 0 && (
            <button
              type='button'
              onClick={() => onChange([])}
              className='mb-1 w-full rounded px-2 py-1 text-left text-xs text-primary hover:bg-accent'
            >
              {t('list.clientFilterClear')}
            </button>
          )}

          <div className='max-h-64 overflow-y-auto'>
            {isError ? (
              <p className='px-2 py-3 text-center text-sm text-destructive'>
                {t('list.clientFilterError')}
              </p>
            ) : clients.length === 0 && !isFetching ? (
              <p className='px-2 py-3 text-center text-sm text-muted-foreground'>
                {t('list.clientFilterEmpty')}
              </p>
            ) : (
              clients.map(c => (
                <label
                  key={c.id}
                  className='flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent'
                >
                  <Checkbox
                    checked={selected.includes(c.id)}
                    onCheckedChange={() => toggle(c.id)}
                  />
                  <ClientNameWithProspect
                    name={c.name}
                    category={c.category}
                    className='min-w-0 flex-1'
                  />
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default ClientMultiSelectFilter
